import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { UserService } from '../../core/user/user.service';
import { User, Password } from '../../core/user/user.types';

export interface UserStateModel {
  user: User | null;
  loading: boolean;
  error: string | null;
  dataLoaded: boolean; // Додаємо флаг, що дані вже були завантажені
}

export class FetchUserData {
  static readonly type = '[User] Fetch User Data';
  constructor(public forceRefresh: boolean = false) {} // Додаємо опцію для примусового оновлення
}

export class UpdateUser {
  static readonly type = '[User] Update User';
  constructor(public payload: Partial<User>) {}
}

export class UpdateUserPassword {
  static readonly type = '[User] Update User Password';
  constructor(public payload: Password) {}
}

export class SendSupportMessage {
  static readonly type = '[User] Send Support Message';
  constructor(public message: string) {}
}

@State<UserStateModel>({
  name: 'user',
  defaults: {
    user: null,
    loading: false,
    error: null,
    dataLoaded: false
  }
})
@Injectable()
export class UserState {
  constructor(private userService: UserService) {}

  @Selector()
  static getUser(state: UserStateModel): User | null {
    return state.user;
  }

  @Selector()
  static getUserEmail(state: UserStateModel): string | null {
    return state.user?.email || null;
  }

  @Selector()
  static isLoading(state: UserStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getError(state: UserStateModel): string | null {
    return state.error;
  }

  @Selector()
  static isDataLoaded(state: UserStateModel): boolean {
    return state.dataLoaded;
  }

  @Action(FetchUserData)
  fetchUserData(ctx: StateContext<UserStateModel>, action: FetchUserData) {
    const state = ctx.getState();

    // Якщо дані вже завантажені і примусове оновлення не потрібне - не робимо запит
    if (state.dataLoaded && state.user && !action.forceRefresh) {
      return of(state.user);
    }

    ctx.patchState({ loading: true, error: null });

    return this.userService.getUserData().pipe(
      tap(user => {
        ctx.patchState({
          user,
          loading: false,
          dataLoaded: true // Позначаємо, що дані були завантажені
        });
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to fetch user data',
          loading: false
        });
        return throwError(() => error);
      })
    );
  }

  @Action(UpdateUser)
  updateUser(ctx: StateContext<UserStateModel>, action: UpdateUser) {
    ctx.patchState({ loading: true, error: null });

    return this.userService.updateUser(action.payload as User).pipe(
      tap(updatedUser => {
        const state = ctx.getState();
        ctx.patchState({
          user: { ...state.user, ...updatedUser },
          loading: false
        });
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to update user data',
          loading: false
        });
        return throwError(() => error);
      })
    );
  }

  @Action(UpdateUserPassword)
  updateUserPassword(ctx: StateContext<UserStateModel>, action: UpdateUserPassword) {
    ctx.patchState({ loading: true, error: null });

    return this.userService.updateUserPassword(action.payload).pipe(
      tap(response => {
        ctx.patchState({ loading: false });
        return response;
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to update password',
          loading: false
        });
        return throwError(() => error);
      })
    );
  }

  @Action(SendSupportMessage)
  sendSupportMessage(ctx: StateContext<UserStateModel>, action: SendSupportMessage) {
    // Here we would call the support service to send a message
    // This is a placeholder as we don't have the direct implementation from the service
    // ctx.patchState({ loading: true });
    //
    // return this.userService.get().pipe(
    //   // This is a placeholder, in reality we would call supportService.sendSupportMessage()
    //   // For now we're just using the get() method to simulate a call
    //   tap(() => {
    //     ctx.patchState({ loading: false });
    //   }),
    //   catchError(error => {
    //     ctx.patchState({
    //       error: error.message || 'Failed to send support message',
    //       loading: false
    //     });
    //     return throwError(() => error);
    //   })
    // );
  }
}
